import type {
  CreateTrialAccountCommand,
    CancelRegistrationCheckoutResult,
  CreateRegistrationSessionResult,
  GetRegistrationStatusResult,
  RegistrationStatus,
  StartRegistrationCheckoutCommand,
  StartRegistrationCheckoutResult,
} from "@/domain/model/Registration";
import type { TokenBundle } from "@/domain/model/Auth";
import type {
    CancelRegistrationCheckoutResponseDto,
  CreateRegistrationSessionResponseDto,
  CreateTrialAccountRequestDto,
  CreateTrialAccountResponseDto,
  RegistrationStatusResponseDto,
  StartRegistrationCheckoutRequestDto,
  StartRegistrationCheckoutResponseDto,
} from "../dto/RegistrationDto";

export function mapRegistrationStatus(value: string): RegistrationStatus {
  switch (value) {
    case "INITIATED":
    case "CHECKOUT_CREATED":
    case "CHECKOUT_COMPLETED":
    case "ACTIVATING":
    case "ACTIVE":
    case "FAILED":
    case "EXPIRED":
    case "CANCELED":
      return value;
    default:
      throw new Error(`Unknown registration status: ${value}`);
  }
}

export function startRegistrationCheckoutCommandToDto(
  command: StartRegistrationCheckoutCommand,
): StartRegistrationCheckoutRequestDto {
  return {
    accountName: command.accountName,
    ownerFullName: command.ownerFullName,
    email: command.email,
    password: command.password,

    billingCompanyName: command.billingCompanyName,
    billingStreet: command.billingStreet,
    billingPostalCode: command.billingPostalCode,
    billingCity: command.billingCity,
    billingCountry: command.billingCountry,
    vatId: command.vatId ?? null,

    seatCount: command.seatCount,
    preferredPaymentMethod: command.preferredPaymentMethod,

    acceptedTermsVersion: command.acceptedTermsVersion,
    acceptedPrivacyVersion: command.acceptedPrivacyVersion,
    acceptedAvvVersion: command.acceptedAvvVersion,
    acceptedUserTermsVersion: command.acceptedUserTermsVersion,
  };
}

export function startRegistrationCheckoutResponseDtoToResult(
  dto: StartRegistrationCheckoutResponseDto,
): StartRegistrationCheckoutResult {
  return {
    registrationId: dto.registrationId,
    checkoutUrl: dto.checkoutUrl,
    expiresAt: dto.expiresAt ? new Date(dto.expiresAt) : null,
    status: mapRegistrationStatus(dto.status),
  };
}

export function registrationStatusResponseDtoToResult(
  dto: RegistrationStatusResponseDto,
): GetRegistrationStatusResult {
  return {
    registrationId: dto.registrationId,
    status: mapRegistrationStatus(dto.status),
    message: dto.message,
    accountId: dto.accountId,
    canRetry: dto.canRetry,
    pollAfterMs: dto.pollAfterMs,
  };
}

export function mapCreateRegistrationSessionResponseDtoToResult(
  dto: CreateRegistrationSessionResponseDto,
): CreateRegistrationSessionResult {
  return {
    accountId: dto.accountId,
    userId: dto.userId,
    accessToken: dto.accessToken,
    accessExpiresAt: dto.accessExpiresAt,
  };
}

export function mapCancelRegistrationCheckoutResponseDtoToResult(
  dto: CancelRegistrationCheckoutResponseDto,
): CancelRegistrationCheckoutResult {
  return {
    registrationId: dto.registrationId,
    status: dto.status,
    canRetry: dto.canRetry
  };
}


export function createTrialAccountCommandToDto(
  command: CreateTrialAccountCommand,
): CreateTrialAccountRequestDto {
  return {
    email: command.email,
    password: command.password,
    ownerName: command.ownerName,
    accountName: command.accountName,
    seats: command.seats,
  };
}

export function createTrialAccountResponseDtoToResult(
  dto: CreateTrialAccountResponseDto,
): TokenBundle {
  const tokens = {
    accessToken: dto.accessToken,
    accessExpiresAt: dto.accessExpiresAt,
    refreshToken: dto.refreshToken ?? null,
    refreshExpiresAt: dto.refreshExpiresAt ?? null,
  };
  return tokens;
}
